/**
 * 숫자/면적/주소 포맷 유틸리티 (F-2.1 / F-2.2).
 *
 * - formatArea       : ㎡/평 등의 면적 값을 소수점 2자리(셋째자리 반올림) + 천단위 콤마 문자열.
 * - formatNumber     : 일반 숫자에 천단위 콤마 (기존 $filters.money 의 null→"0" 버그 수정판).
 * - formatMoneyMuch  : 만원 단위 숫자를 "N억 M만원" 포맷으로. 10000만원 미만은 "M만원",
 *                      remainder=0 이면 억 표기만, 0 은 "0만원".
 * - normalizeAddr    : "서울특별시 … 번지" → "서울 … " 의 단축 주소 변환(F-2.1).
 *                      백엔드 `shortAdr` 의 핵심 로직 중 프론트엔드 렌더 경로에서
 *                      자주 필요한 두 가지 치환(서울특별시→서울, 번지 제거)만 담당.
 *
 * 2026-04-15 업데이트 — seon.js 의 `$formatMoney(_, MUCH | LOAN)` 도 이제
 * 같은 spec 포맷("53억 202만원")을 내보낸다. `formatMoneyMuch` 는 여전히
 * 독립 export 로 유지(테스트 및 DB 스키마 명시적 "만원 단위" 주석 때문).
 */

// ===== Ratio (건폐율·용적률) =====

/**
 * 건폐율·용적률을 "59.27%" 로 낸다. 값이 없거나 0 이면 "-" 다(DD, 2026-10-17).
 * 0 은 '값 없음' 이다: 대장에도 계산에도 값이 없어 비운 매물이 0 이고, 수정 화면 저장이 그 0 을 NULL 로
 * 바꾼다. 그대로 찍으면 상세 화면은 "0%" 나 "%", 문자열로 이어 붙이는 연쇄 출력은 "null%" 가 나왔다.
 *
 * @param {number|string|null|undefined} value
 * @returns {string} "59.27%" 또는 "-"
 */
export function formatRatio(value) {
	if (value === null || value === undefined || value === '') return '-';
	const num = typeof value === 'number' ? value : parseFloat(value);
	if (!Number.isFinite(num) || num <= 0) return '-';
	return num + '%';
}

// ===== Area (㎡/평) =====

/**
 * 면적 숫자를 소수점 2자리로 반올림한 문자열을 반환한다.
 * 소수점 셋째자리에서 round-half-up (away-from-zero) 규칙으로 반올림된다.
 * 정수부에는 천단위 콤마가 삽입된다 (F-2.2 spec: "1,234.56").
 * 빈 값 / 유한하지 않은 값 (NaN, Infinity 등) 은 빈 문자열을 반환한다.
 *
 * Number#toFixed(2) 는 IEEE 754 이진 부동소수점 표현 오차로 인해 half case 에서
 * 아래로 내려가는 경우가 있다 (예: (10.075).toFixed(2) === "10.07",
 * (530202.345).toFixed(2) === "530202.34"). EPSILON 보정 + Math.round 방식도
 * 10.075 같은 케이스에서 실패하며, 음수 half 값은 0 쪽으로 반올림된다.
 *
 * 따라서 toFixed(3) 로 셋째자리까지 문자열화한 뒤, 그 셋째자리 숫자를 보고
 * 정수 산술로 둘째자리에서 round-half-up 을 수행한다. toFixed(3) 는 IEEE-754
 * 오차가 있더라도 4번째 저장 digit 의 반올림이 3번째 digit 을 회복시켜 주기
 * 때문에 (10.074999... → "10.075") half case 신호가 유실되지 않는다.
 *
 * 천단위 콤마는 `toLocaleString({minimumFractionDigits:2})` 대신 위 수동
 * 반올림 결과의 정수부에만 regex 로 적용한다 — `toLocaleString` 은 내부적으로
 * 원래 수 값을 반올림하기 때문에 동일한 half-case 버그에 다시 노출될 수 있다.
 *
 * @param {number|string|null|undefined} value
 * @returns {string} "1,234.56" 형식 문자열, 또는 빈 값일 때 ""
 */
export function formatArea(value) {
	if (value === null || value === undefined || value === '') return '';
	const num = typeof value === 'number' ? value : parseFloat(value);
	if (!Number.isFinite(num)) return '';

	// 셋째자리 반올림 (round-half-up away from zero) at the 2nd decimal.
	// Using toFixed(3) then manual integer arithmetic avoids the IEEE-754
	// half-case bug where Number#toFixed(2) rounds 10.075 → "10.07" instead
	// of "10.08". toFixed(3) on 10.075 returns "10.075" (because the 4th
	// stored digit rounds up), so inspecting the 3rd digit gives a reliable
	// rounding signal.
	const abs = Math.abs(num);
	const fixed3 = abs.toFixed(3);
	const dotIdx = fixed3.indexOf('.');
	const intPart = fixed3.slice(0, dotIdx);
	const decPart = fixed3.slice(dotIdx + 1);
	const first2 = decPart.substring(0, 2);
	const third = decPart.charAt(2);

	let roundedInt;
	let roundedDec;
	if (third >= '5') {
		// Round up: integer arithmetic on the concatenated int+first2 value
		// (e.g. "10"+"07" → 1007, +1 → 1008 → intPart="10", decPart="08")
		const rounded = Number(intPart + first2) + 1;
		const roundedStr = rounded.toString().padStart(3, '0');
		roundedInt = roundedStr.slice(0, -2);
		roundedDec = roundedStr.slice(-2);
	} else {
		roundedInt = intPart;
		roundedDec = first2;
	}

	// F-2.2: 천단위 콤마 (정수부에만). 소수점 보존.
	const intWithCommas = roundedInt.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
	const result = intWithCommas + '.' + roundedDec;

	return (num < 0 ? '-' : '') + result;
}

// ===== Money =====

/**
 * 일반 숫자에 천단위 콤마를 적용해 문자열로 반환한다.
 * 빈 값 / NaN 은 빈 문자열을 반환한다.
 *
 * (기존 main.js 의 $filters.money 는 null/빈 값에 대해 숫자 0 을 반환해 셀에
 *  "0" 이 표시되는 버그가 있었다. 본 함수는 빈 문자열을 반환하여 이를 수정한다.)
 *
 * @param {number|string|null|undefined} value
 * @returns {string} "1,234,567" 형식, 또는 빈 값일 때 ""
 */
export function formatNumber(value) {
	if (value === null || value === undefined || value === '') return '';
	const num = typeof value === 'number' ? value : Number(value);
	if (!Number.isFinite(num)) return '';
	return num.toLocaleString();
}

/**
 * 만원 단위 정수를 "N억 M만원" 포맷 문자열로 반환한다 (F-2.2 spec).
 *
 * 규칙:
 *   - 입력은 **만원 단위** 정수 (DB 상 sell_price, deposit 등의 실제 스키마).
 *   - `10000` 이상 → `{eok}억` (remainder=0) 또는 `{eok}억 {rem}만원` (remainder>0).
 *   - `10000` 미만 → `{n}만원` (0 포함, 즉 `0` → `"0만원"`).
 *   - 음수는 앞에 `-` 부호 부착.
 *   - null / undefined / NaN / 빈 문자열 → `""`.
 *
 * 예시 (spec p.14):
 *   - `530202   → "53억 202만원"`  (530202 / 10000 = 53억, 나머지 202만원)
 *   - `270000   → "27억"`          (remainder=0 이면 "만원" 생략)
 *   - `999      → "999만원"`
 *   - `0        → "0만원"`
 *   - `-50000   → "-5억"`
 *
 * 2026-04-15: seon.js 의 `c(v, MUCH | LOAN)` 도 동일 포맷을 내보내도록
 * 업데이트되었으므로 신규 코드는 양쪽 중 편한 쪽을 쓰면 된다. 본 함수는
 * DB 스키마 주석상 "만원 단위" 라는 계산 의도를 명시할 때 선호된다.
 *
 * @param {number|string|null|undefined} value 만원 단위 숫자
 * @returns {string}
 */
export function formatMoneyMuch(value) {
	if (value === null || value === undefined || value === '') return '';
	const num = typeof value === 'number' ? value : Number(value);
	if (!Number.isFinite(num)) return '';

	const sign = num < 0 ? '-' : '';
	const abs = Math.abs(Math.trunc(num));

	const eok = Math.floor(abs / 10000);
	const rem = abs - eok * 10000;

	let body;
	if (eok === 0) {
		// < 10000 만원 — "만원" suffix 포함. 0 포함.
		body = rem.toLocaleString() + '만원';
	} else if (rem === 0) {
		body = eok.toLocaleString() + '억';
	} else {
		body = eok.toLocaleString() + '억 ' + rem.toLocaleString() + '만원';
	}

	return sign + body;
}

// ===== Address =====

/**
 * 백엔드 `shortAdr` 의 핵심 로직 중 프론트엔드 렌더 경로에서 자주 필요한
 * 세 가지 치환(서울특별시→서울, 서울시→서울, 번지 제거) 만 수행하는 방어적 래퍼 (F-2.1).
 *
 * 신규 저장 경로는 이미 `exports.shortAdr(...)` 를 거치므로 원시 문자열이
 * 흘러 들어올 수 없지만, 과거에 저장된 legacy row 와 V-World / Naver 가
 * 응답으로 직접 넘겨 주는 값은 여전히 "서울특별시 강남구 … 12번지" 형태이다.
 * 검색/상세/프린트 화면의 주소 ingestion 지점에서 이 함수를 통해 한 번 더
 * 정규화해 주면, DB 재적재 없이도 화면 표기가 일관된다.
 *
 * 전체 광역시/도 축약(부산광역시→부산 등)과 "○○도→○○" 말미 글자 제거는
 * 다루지 않는다 — 프론트 데이터의 99% 가 서울이고, 잘못된 토큰 매칭
 * (예: "강화도" → "강화") 위험을 피하기 위함이다. 필요 시 백엔드에서
 * shortAdr 을 통과한 값이 내려와야 한다.
 *
 * @param {string|null|undefined} addr
 * @returns {string} 정규화된 주소. 빈 입력은 "" 를 반환.
 */
export function normalizeAddr(addr) {
	if (!addr) return '';
	return String(addr)
		.replace(/서울특별시/g, '서울')
		.replace(/서울시/g, '서울')
		// 백엔드 shortAdr 과 동일하게 "번지" 토큰만 제거하고 앞의 숫자는 보존한다
		// ("서울특별시 강남구 개포동 12번지" → "서울 강남구 개포동 12").
		.replace(/번지/g, '')
		.replace(/\s+/g, ' ')
		.trim();
}

/**
 * 연결주소(linkAddr / jinbun_addr) 를 목록 표기용으로 포맷한다.
 *
 * 규칙:
 *   1. 첫 번째 주소는 그대로 둔다 (시·구·동·번지 모두 유지).
 *   2. 두 번째 이후 주소는 첫 주소와 "<시/도 [+ 시] + 구>" 접두가 동일할 때만
 *      해당 접두를 제거해 "동 번지..." 만 남긴다. 접두가 다르면 원문을 그대로
 *      유지해 구/시 정보가 사라지지 않도록 한다.
 *   3. 첫 주소가 "서울 강남구 ..." (광역시/서울: 시+구) 또는 "경기 성남시 분당구
 *      ..." (도: 도+시+구) 패턴과 일치하지 않으면 모든 항목을 원문 그대로 결합한다.
 *   4. 입력 배열은 normalizeAddr 로 이미 "서울특별시 → 서울", "번지" 제거 된
 *      상태라고 가정한다.
 *
 * 예시 (bid=25624 청담동 4필지):
 *   ["서울 강남구 청담동 41", "서울 강남구 청담동 41-1",
 *    "서울 강남구 청담동 41-5", "서울 강남구 청담동 41-6"]
 *   → "서울 강남구 청담동 41, 청담동 41-1, 청담동 41-5, 청담동 41-6"
 *
 * 다른 구 혼재:
 *   ["서울 강남구 역삼동 1", "서울 서초구 양재동 2"]
 *   → "서울 강남구 역삼동 1, 서울 서초구 양재동 2" (서초구 정보 보존)
 *
 * 도 지역 ("도 시 구 동"):
 *   ["경기 성남시 분당구 삼평동 1", "경기 성남시 분당구 삼평동 2"]
 *   → "경기 성남시 분당구 삼평동 1, 삼평동 2"
 *
 * formatBodyAddr 와의 차이 — formatBodyAddr 는 PDF 본문 전용이라 모든 항목에서
 * 시·구를 제거하고 동일 동 3번째부터는 동까지 생략한다. 본 함수는 상세/편집
 * 화면의 연결주소 표기에 맞춰 첫 항목을 풀 주소로 남기고 동은 항상 유지한다.
 *
 * @param {string[]} uniqueAddrs normalizeAddr 로 정규화·dedup 된 주소 배열
 * @returns {string} 콤마로 결합된 연결주소. 빈 배열은 "" 반환.
 */
export function formatLinkAddr(uniqueAddrs) {
	if (!Array.isArray(uniqueAddrs) || uniqueAddrs.length === 0) return '';
	const first = String(uniqueAddrs[0] || '').replace(/\s+/g, ' ').trim();
	if (uniqueAddrs.length === 1) return first;

	// 첫 주소에서 "<시/도> [<시>] <구>" 접두를 잡는다 (끝 공백 포함).
	// 1) "도 시 구" 3-토큰 우선 시도 (경기 성남시 분당구)
	// 2) "시 구"    2-토큰 시도       (서울 강남구, 부산 해운대구 등)
	// 매치 실패 시 접두 제거 불가 — 원문을 그대로 결합한다.
	let prefix = null;
	const m3 = first.match(/^(\S+\s+\S+시\s+\S+구)\s+/);
	const m2 = first.match(/^(\S+\s+\S+구)\s+/);
	if (m3) prefix = m3[1] + ' ';
	else if (m2) prefix = m2[1] + ' ';

	if (!prefix) return uniqueAddrs.map(a => String(a || '').replace(/\s+/g, ' ').trim()).join(', ');

	const rest = uniqueAddrs.slice(1).map(addr => {
		const norm = String(addr || '').replace(/\s+/g, ' ').trim();
		// 첫 주소와 "<시/도 [+시] + 구>" 접두가 정확히 일치할 때만 접두 제거.
		// 구(區)가 다르면 원문 유지 — "서울 강남구 역삼동 1, 서울 서초구 양재동 2"
		// 에서 두 번째를 "양재동 2" 로 축약하면 서초구 정보가 사라지기 때문.
		if (norm.startsWith(prefix)) return norm.slice(prefix.length);
		return norm;
	});

	return [first, ...rest].join(', ');
}

/**
 * 출력 PDF 본문에 표시될 다중 필지 주소를 포맷한다 (F-5.2).
 *
 * PDF spec (docs/bug-reports/) 샘플:
 *   입력: 서울 강남구 청담동 41 · 41-1 · 41-5 · 41-6 (4 필지)
 *   표지: "서울 강남구 청담동 41 외 3필지" (coverAddr, 기존 구현)
 *   본문: "청담동 41, 청담동 41-1, 41-5, 41-6" (bodyAddr, 본 함수)
 *
 * 규칙 ("상위 주소 중복 생략"):
 *   1. 각 항목에서 시/구는 항상 제거한다 ("서울 강남구" 제거).
 *   2. 동일 동(同洞)의 첫 2개 항목은 "동 번지" 형태로 유지한다
 *      — 독자에게 어느 동인지 두 번 확인시켜 주기 위함.
 *   3. 동일 동의 3번째 이후 항목은 동을 생략하고 "번지"만 출력한다.
 *   4. 동이 바뀌면 카운터가 리셋되어 새 동의 첫 2개 항목은 다시 동을 유지한다.
 *   5. 파싱이 불가능한 값 (동 토큰 없음)은 원문 그대로 유지한다.
 *
 * 예시 (bid=25624 청담동 4필지):
 *   ["서울 강남구 청담동 41", "서울 강남구 청담동 41-1",
 *    "서울 강남구 청담동 41-5", "서울 강남구 청담동 41-6"]
 *   → "청담동 41, 청담동 41-1, 41-5, 41-6"
 *
 * @param {string[]} uniqueAddrs normalizeAddr 로 정규화된 주소 배열 (dedup 완료 상태)
 * @returns {string} 콤마로 결합된 본문 주소. 빈 배열은 "" 반환.
 */
export function formatBodyAddr(uniqueAddrs) {
	if (!Array.isArray(uniqueAddrs) || uniqueAddrs.length === 0) return '';

	// "<시> <구> <동> <번지...>" 패턴 분해.
	// 구/동은 접미어(구/동)로 끝나는 토큰, 시는 그 앞의 첫 토큰(서울 등).
	// 파싱 불가 시 원문을 그대로 사용 (fallback).
	function parse(addr) {
		const norm = String(addr || '').replace(/\s+/g, ' ').trim();
		const m = norm.match(/^(\S+)\s+(\S+구)\s+(\S+동)\s+(.+)$/);
		if (!m) return { raw: norm };
		return { si: m[1], gu: m[2], dong: m[3], jibun: m[4], raw: norm };
	}

	const parts = uniqueAddrs.map(parse);
	const out = [];
	// 동일 동(同洞) 연속 출현 카운터. 동이 바뀌면 1로 리셋된다.
	// 1, 2 번째는 "동 번지", 3 번째 이후는 "번지" 만 출력 (상위 주소 중복 생략).
	let prevDong = null;
	let sameDongRun = 0;
	for (let i = 0; i < parts.length; i++) {
		const p = parts[i];
		if (!p.dong) {
			// 파싱 실패 — 원문 유지. 동 컨텍스트는 리셋 (다음 동이 새로 시작).
			out.push(p.raw);
			prevDong = null;
			sameDongRun = 0;
			continue;
		}
		if (p.dong === prevDong) {
			sameDongRun++;
		} else {
			prevDong = p.dong;
			sameDongRun = 1;
		}
		if (sameDongRun <= 2) {
			out.push(p.dong + ' ' + p.jibun);
		} else {
			out.push(p.jibun);
		}
	}
	return out.join(', ');
}
